import React from 'react';
import TopTitleBlock from '../../Components/TopTitleBlock/TopTitleBlock';
import UserInfo from '../../Components/User/UserInfo';
import s from './MyOrdersForManager.module.css';
import {useTranslation} from 'react-i18next';
import UserDeferred from './components/UserDeferred';
import {
	useOrderActivityListQuery,
	useOrdersListQuery,
	useOrdersListStoreQuery,
} from '../../redux/userOrdersApi';
import {RequestOrdersDataType} from '../../types/TypesUserOrders';
import PreLoaderComponent from '../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import MyOrdersNewApiTable from './components/MyOrdersNewApiTable';
import MyOrdersSale from './MyOrdersSale';
import TopPageInfoBlock from '../../Components/TopPageInfoBlock/TopPageInfoBlock';
import {useStatusRestriction} from '../../hooks/useStatusRestriction';
import StatusRestrictionMessage from '../../Components/StatusRestrictionMessage/StatusRestrictionMessage';
import {monthNow} from '../../assets/utils/dateUtils';
import {
	getDaysInMonth,
	formatDateFromMonthAndDay,
} from '../../hooks/useMonthRangeFilter';

const MyOrdersNewApi: React.FC = () => {
	const {t} = useTranslation();
	const restrictions = useStatusRestriction();

	// Используем утилиты из хука для формирования начальных дат
	const defaultMonthForOrders = monthNow();
	const defaultDaysInMonthForOrders = getDaysInMonth(defaultMonthForOrders);
	const defaultFromDateForOrders = formatDateFromMonthAndDay(
		defaultMonthForOrders,
		1,
	);
	const defaultToDateForOrders = formatDateFromMonthAndDay(
		defaultMonthForOrders,
		defaultDaysInMonthForOrders,
	);

	const [filters, setFilters] = React.useState<RequestOrdersDataType>({
		filter_active: 0,
		offset: 0,
		from_date: defaultFromDateForOrders,
		to_date: defaultToDateForOrders,
	});
	const [filtersStore, setFiltersStore] = React.useState<RequestOrdersDataType>(
		{
			filter_active: 0,
			offset: 0,
		},
	);
	// Функция для получения первого и последнего дня месяца
	const getMonthStartAndEnd = (monthString: string) => {
		if (!monthString) return {from_date: '', to_date: ''};

		const date = new Date(monthString);
		const year = date.getFullYear();
		const month = date.getMonth();

		// Первый день месяца
		const firstDay = new Date(year, month, 1);
		const from_date = `${year}-${(month + 1).toString().padStart(2, '0')}-01`;

		// Последний день месяца
		const lastDay = new Date(year, month + 1, 0);
		const to_date = `${year}-${(month + 1).toString().padStart(2, '0')}-${lastDay.getDate().toString().padStart(2, '0')}`;

		return {from_date, to_date};
	};

	const defaultMonth = monthNow();
	const defaultActivityDates = getMonthStartAndEnd(defaultMonth);

	const [filtersActivity, setFiltersActivity] =
		React.useState<RequestOrdersDataType>({
			filter_active: 0,
			offset: 0,
			from_date: defaultActivityDates.from_date,
			to_date: defaultActivityDates.to_date,
		});
	const {data: ordersList, isLoading: isLoadingOrdersList} = useOrdersListQuery(
		filters,
		{
			skip: !restrictions.canAccessMyOrdersPage,
		},
	);
	const {data: storeList, isLoading: isLoadingStoreList} =
		useOrdersListStoreQuery(filtersStore, {
			skip:
				!restrictions.canAccessMyOrdersPage ||
				!restrictions.canShowOrdersByOthers,
		});

	const {data: activityList, isLoading: isLoadingActivityList, refetch: refetchActivityList} =
		useOrderActivityListQuery(filtersActivity, {
			skip:
				!restrictions.canAccessMyOrdersPage ||
				!restrictions.canShowOrdersByOthers,
		});

	const activityListRef = React.useRef<HTMLDivElement>(null);

	if (!restrictions.canAccessMyOrdersPage) {
		return (
			<div className="page__content">
				<StatusRestrictionMessage message={t('Сторінка недоступна')} />
			</div>
		);
	}

	return (
		<div className="page__content">
			<TopTitleBlock title={'Список моїх замовлень'} />

			<div className="page__user-profile user-profile">
				<div className="user__row">
					<div className="user__column">
						<UserInfo />
					</div>
					{restrictions.canShowDeferredPoints && (
						<div className="user__column">
							<UserDeferred 
								onSaveSuccess={() => {
									// Обновляем таблицу
									refetchActivityList();
									// Скроллим к секции после небольшой задержки
									setTimeout(() => {
										activityListRef.current?.scrollIntoView({
											behavior: 'smooth',
											block: 'start',
										});
									}, 100);
								}}
							/>
						</div>
					)}
				</div>
			</div>
			<div className={s.infoContainer}>
				<TopPageInfoBlock
					text={t(
						'“Перша таблиця - це створені вами замовлення, друга таблиця - це ваші активності проведені іншими клієнтами, третя таблиця - ваші проведені активності на інших клієнтів. ',
					)}
					subText={t(
						'За замовчуванням показані замовлення за поточний та минулий місяць. Також у фільтрі ви можете обрати потрібний місяць, або в полі пошук ввести номер замовлення чи назву товару',
					)}
				/>
			</div>

			{isLoadingOrdersList ? (
				<PreLoaderComponent type={'circle'} size={'big'} />
			) : (
				ordersList?.orders && (
					<MyOrdersNewApiTable
						type={'order'}
						setFilters={setFilters}
						data={ordersList}
						columns={[
							'',
							'ID',
							t('ДАТА СТВОРЕННЯ'),
							t('Вартість'),
							t('СТАТУС ДОСТАВКИ'),
							t('СТАТУС ОПЛАТИ'),
							t('Дії'),
							t('Вид замовлення'),
						]}
					/>
				)
			)}
			{restrictions.canShowOrdersByOthers && (
				<div className={s.subTitle}>
					{t('Список оформлень іншими клієнтами')}
				</div>
			)}
			{restrictions.canShowOrdersByOthers && isLoadingStoreList ? (
				<PreLoaderComponent type={'circle'} size={'big'} />
			) : (
				restrictions.canShowOrdersByOthers &&
				storeList?.orders && (
					<MyOrdersNewApiTable
						type={'list-store'}
						setFilters={setFiltersStore}
						data={storeList}
						columns={[
							'',
							'ID',
							t('ДАТА СТВОРЕННЯ'),
							t('Активність'),
							t('Склад'),
							t('Статус'),
							t('Дії'),
							t('Оформлювач'),
						]}
					/>
				)
			)}
			{restrictions.canShowOrdersByOthers && (
				<div className={s.subTitle} ref={activityListRef}>
					{t('Список оформлень на інших клієнтів')}
				</div>
			)}
			{restrictions.canShowOrdersByOthers && (
				<div className={s.infoContainer}>
					{isLoadingActivityList ? (
						<PreLoaderComponent type={'circle'} size={'big'} />
					) : (
						activityList?.activities && (
							<MyOrdersSale
								setFilters={setFiltersActivity}
								data={activityList}
							/>
						)
					)}
				</div>
			)}
		</div>
	);
};

export default MyOrdersNewApi;
